import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Star, StarFilled } from "@digital-twins/design-system/icons";
import type { CreativeSubjectReferenceView } from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SubjectReferenceGalleryProps } from "@/types/creative-library";
import { PrivateImage } from "./PrivateImage";

export function SubjectReferenceGallery({
  photos,
  references,
  primary,
  views,
  canEdit,
  saving,
  unavailable,
  onReferencesChange,
  onPrimaryChange,
  onViewsChange,
}: SubjectReferenceGalleryProps) {
  const { t } = useTranslation("app");
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {photos.map((photo) => {
        const linked = references.includes(photo.token);
        const main = primary === photo.token;
        return (
          <div
            key={photo.token}
            className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-background p-3"
          >
            <PrivateImage
              source={photo.mediaUrl}
              alt={photo.title}
              className="h-24 w-full rounded-lg object-contain"
            />
            <Text size="xs" className="break-words">
              {photo.title}
            </Text>
            <label className="flex items-start gap-2">
              <Checkbox
                checked={linked}
                aria-label={t("creativeLibrary.inCharacterGalleryFor", {
                  name: photo.title,
                })}
                disabled={
                  !canEdit ||
                  saving ||
                  unavailable ||
                  (!linked && references.length >= 20)
                }
                onCheckedChange={(checked) =>
                  onReferencesChange(
                    checked === true
                      ? [...references, photo.token]
                      : references.filter((token) => token !== photo.token),
                  )
                }
                data-testid={`subject-gallery-${photo.token}`}
              />
              <Text size="xs">{t("creativeLibrary.inCharacterGallery")}</Text>
            </label>
            {linked && (
              <>
                {(canEdit || main) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-auto min-h-9 w-full gap-2 whitespace-normal px-2 py-2 text-xs",
                      main && "border-primary text-primary",
                    )}
                    disabled={!canEdit || saving}
                    onClick={() => onPrimaryChange(main ? null : photo.token)}
                    aria-pressed={main}
                    aria-label={t(
                      main
                        ? "creativeLibrary.clearMainReferenceFor"
                        : "creativeLibrary.setMainReferenceFor",
                      { name: photo.title },
                    )}
                    data-testid={`subject-primary-${photo.token}`}
                  >
                    {main ? (
                      <StarFilled className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    ) : (
                      <Star className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    )}
                    {t(
                      main
                        ? "creativeLibrary.mainReference"
                        : "creativeLibrary.setMainReference",
                    )}
                  </Button>
                )}
                <Select
                  value={views[photo.token] ?? "unspecified"}
                  disabled={!canEdit || saving || unavailable}
                  onValueChange={(view) => {
                    const next = { ...views };
                    if (view === "unspecified") delete next[photo.token];
                    else
                      next[photo.token] = view as CreativeSubjectReferenceView;
                    onViewsChange(next);
                  }}
                >
                  <SelectTrigger
                    className="h-9 min-w-0 text-xs"
                    aria-label={t("creativeLibrary.referenceViewFor", {
                      name: photo.title,
                    })}
                    data-testid={`subject-view-${photo.token}`}
                  >
                    <SelectValue
                      placeholder={t("creativeLibrary.referenceView")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      [
                        "unspecified",
                        "front",
                        "three_quarter",
                        "profile",
                        "full_body",
                        "other",
                      ] as const
                    ).map((view) => (
                      <SelectItem key={view} value={view}>
                        {t(`creativeLibrary.referenceViews.${view}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
